/**
 * Custom hook for managing recording handlers in public recording
 */

import type { PublicRecordingService } from '@/lib/public-recording-service';
import { useCallback } from 'react';
import { useToast } from './use-toast';

interface UsePublicRecordingHandlersParams {
  recordingServiceRef: React.MutableRefObject<PublicRecordingService | null>;
  scribeJobId: string | null;
  appointmentId: string;
  patientId: string;
  locationId: string;
  sectionTag: string;
  clientPlatform?: string;
  patientCode: string;
  gatewayToken: string | null;
  setIsRecording: (value: boolean) => void;
  setIsPaused: (value: boolean) => void;
  setIsUploading: (value: boolean) => void;
  setIsProcessing: (value: boolean) => void;
  setError: (value: string | null) => void;
  latestDxListRef: React.MutableRefObject<any>;
}

export const usePublicRecordingHandlers = ({
  recordingServiceRef,
  scribeJobId,
  appointmentId,
  patientId,
  locationId,
  sectionTag,
  clientPlatform,
  patientCode,
  gatewayToken,
  setIsRecording,
  setIsPaused,
  setIsUploading,
  setIsProcessing,
  setError,
  latestDxListRef,
}: UsePublicRecordingHandlersParams) => {
  const { toast } = useToast();

  // Start recording
  const handleStartRecording = useCallback(async () => {
    console.log('[PublicAiScribe] Start button clicked');

    if (!scribeJobId) {
      console.error('[PublicAiScribe] Cannot start: scribeJobId is missing');
      const errorMsg =
        'Recording session not initialized. Please refresh the page.';
      setError(errorMsg);
      toast({
        title: 'Not Ready',
        description: errorMsg,
        variant: 'destructive',
        duration: 2000, // 2 seconds
      });
      return;
    }

    if (!recordingServiceRef.current) {
      console.error(
        '[PublicAiScribe] Cannot start: recording service not initialized'
      );
      const errorMsg =
        'Recording service not available. Please refresh the page.';
      setError(errorMsg);
      toast({
        title: 'Service Error',
        description: errorMsg,
        variant: 'destructive',
        duration: 2000, // 2 seconds
      });
      return;
    }

    try {
      setError(null);
      console.log('[PublicAiScribe] Starting recording with:', {
        appointmentId,
        patientId,
        locationId,
        scribeJobId,
      });

      // Start recording with the public recording service (single-chunk recording)
      await recordingServiceRef.current.startRecording();

      setIsRecording(true);
      setIsPaused(false);

      console.log('[PublicAiScribe] ✅ Recording started successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[PublicAiScribe] Failed to start recording:', {
        error: errorMessage,
        details: err,
      });

      // Check if it's a permission error
      const isPermissionError =
        errorMessage.toLowerCase().includes('permission') ||
        errorMessage.toLowerCase().includes('notallowed');

      const userErrorMsg = isPermissionError
        ? 'Microphone access denied. Please allow microphone permissions and try again.'
        : `Failed to start recording: ${errorMessage}`;

      setError(userErrorMsg);
      toast({
        title: 'Recording Failed',
        description: userErrorMsg,
        variant: 'destructive',
        duration: 2000, // 2 seconds
      });
    }
  }, [
    scribeJobId,
    appointmentId,
    patientId,
    locationId,
    recordingServiceRef,
    setError,
    setIsRecording,
    setIsPaused,
    toast,
  ]);

  // Pause recording
  const handlePauseRecording = useCallback(async () => {
    console.log('[PublicAiScribe] Pause button clicked');

    if (!recordingServiceRef.current) {
      console.error(
        '[PublicAiScribe] Cannot pause: recording service not available'
      );
      return;
    }

    try {
      console.log('[PublicAiScribe] Pausing recording (no upload)...');

      // Pause recording - no upload, just pause
      await recordingServiceRef.current.pauseRecording();
      setIsPaused(true);

      console.log('[PublicAiScribe] ✅ Recording paused');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[PublicAiScribe] Failed to pause:', errorMessage, err);
      toast({
        title: 'Pause Failed',
        description: `Failed to pause recording: ${errorMessage}`,
        variant: 'destructive',
        duration: 2000, // 2 seconds
      });
    }
  }, [recordingServiceRef, setIsPaused, toast]);

  // Resume recording
  const handleResumeRecording = useCallback(async () => {
    console.log('[PublicAiScribe] Resume button clicked');

    if (!recordingServiceRef.current) {
      console.error(
        '[PublicAiScribe] Cannot resume: recording service not available'
      );
      return;
    }

    try {
      console.log('[PublicAiScribe] Resuming recording...');

      // Resume recording
      await recordingServiceRef.current.resumeRecording();
      setIsPaused(false);

      console.log('[PublicAiScribe] ✅ Recording resumed');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[PublicAiScribe] Failed to resume:', errorMessage, err);
      toast({
        title: 'Resume Failed',
        description: `Failed to resume recording: ${errorMessage}`,
        variant: 'destructive',
        duration: 2000, // 2 seconds
      });
    }
  }, [recordingServiceRef, setIsPaused, toast]);

  // Stop recording
  const handleStopRecording = useCallback(async () => {
    console.log('[PublicAiScribe] Stop button clicked');

    if (!recordingServiceRef.current) {
      console.error(
        '[PublicAiScribe] Cannot stop: recording service not available'
      );
      return;
    }

    if (!scribeJobId) {
      console.error('[PublicAiScribe] Cannot stop: scribeJobId is missing');
      return;
    }

    try {
      // Set uploading state
      setIsRecording(false);
      setIsPaused(false);
      setIsUploading(true);

      console.log('[PublicAiScribe] Stopping and uploading recording...');

      // Get the latest message data if available
      const dxList = latestDxListRef.current;

      // Stop recording and upload (single chunk)
      const uploadResult = await recordingServiceRef.current.stopRecording(
        {
          appointmentId,
          patientId,
          locationId,
          scribeJobId,
          sectionTag,
          clientPlatform,
          patientCode,
          authToken: gatewayToken || undefined,
        },
        'public-recording', // createdBy
        dxList
      );

      console.log(
        '[PublicAiScribe] ✅ Recording stopped and uploaded successfully:',
        uploadResult
      );

      // Upload complete, now show AI processing state
      setIsUploading(false);
      setIsProcessing(true);

      // Post message to parent window to indicate that the upload is complete
      if (window.parent) {
        window.parent.postMessage(
          {
            type: 'audioUploadComplete',
            payload: 'true',
            sectionTag,
          },
          '*'
        );
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error(
        '[PublicAiScribe] Failed to stop/upload:',
        errorMessage,
        err
      );

      // Reset states on error
      setIsUploading(false);
      setIsProcessing(false);

      toast({
        title: 'Upload Failed',
        description: `Failed to save recording: ${errorMessage}`,
        variant: 'destructive',
        duration: 2000, // 2 seconds
      });
    }
  }, [
    appointmentId,
    patientId,
    locationId,
    scribeJobId,
    sectionTag,
    clientPlatform,
    gatewayToken,
    recordingServiceRef,
    setIsRecording,
    setIsPaused,
    setIsUploading,
    setIsProcessing,
    toast,
    latestDxListRef,
  ]);

  return {
    handleStartRecording,
    handlePauseRecording,
    handleResumeRecording,
    handleStopRecording,
  };
};
